<template>
    <!-- Desktop View (Vertical Sidebar) -->
    <div v-if="!mobile" class="wizard-sidebar">
        <div class="sidebar-header">
            <div class="step-title">Step {{ currentStep }}</div>
            <div class="step-description">{{ stepDescriptions[currentStep - 1] }}</div>
        </div>
        
        <div class="steps-list">
            <div v-for="(step, index) in steps" :key="index" class="step-item">
                <div class="step-row">
                    <div class="step-circle" :class="{
                        'step-circle-active': currentStep === index + 1,
                        'step-circle-completed': currentStep > index + 1
                    }">
                        <v-icon v-if="currentStep > index + 1" small color="white">mdi-check</v-icon>
                        <span v-else>{{ index + 1 }}</span>
                    </div>
                    <span class="step-name" :class="{
                        'step-name-active': currentStep === index + 1,
                        'step-name-completed': currentStep > index + 1
                    }">{{ step.label }}</span>
                </div>
                <div v-if="index < steps.length - 1" class="step-connector" :class="{
                    'step-connector-completed': currentStep > index + 1
                }"></div>
            </div>
        </div>
    </div>

    <!-- Mobile View (Horizontal Stepper) -->
    <div v-else class="wizard-stepper-mobile">
        <div class="mobile-header">
            <div class="progress-info">
                <span class="current-step-text">Step {{ currentStep }} of {{ steps.length }}</span>
            </div>
        </div>

        <!-- Horizontal Progress Bar -->
        <div class="progress-bar-container">
            <div class="progress-bar-bg">
                <div class="progress-bar-fill" :style="{width: `${(currentStep / steps.length) * 100}%`}"></div>
            </div>
        </div>

        <!-- Horizontal Steps -->
        <div class="steps-horizontal">
            <div v-for="(step, index) in steps" :key="index" class="step-item-horizontal">
                <div class="step-circle-horizontal" :class="{
                    'step-circle-active': currentStep === index + 1,
                    'step-circle-completed': currentStep > index + 1,
                    'step-circle-upcoming': currentStep < index + 1
                }">
                    <v-icon v-if="currentStep > index + 1" small color="#994C26">mdi-check</v-icon>
                    <span v-else class="step-number">{{ index + 1 }}</span>
                </div>
                <span class="step-label-horizontal" :class="{
                    'step-label-active': currentStep === index + 1,
                    'step-label-completed': currentStep > index + 1
                }">{{ step.label }}</span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'WizardSidebar',
    props: {
        currentStep: {
            type: Number,
            required: true
        },
        steps: {
            type: Array,
            required: true
        },
        mobile: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        stepDescriptions() {
            return [
                'Let us know a few things to personalize your course experience',
                'Choose a suitable date and time for your free session',
                "Tell us how many classes you want and when you're available",
                'Add any notes or preferences before we confirm your enrollment'
            ];
        }
    }
};
</script>

<style lang="scss" scoped>
// Desktop Vertical Sidebar
.wizard-sidebar {
    width: 280px;
    min-width: 280px;
    background: #994C26;
    border-radius: 8px;
    padding: 28px;
    position: relative;
    border: 4px solid #FFFFFF;
    box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.13);
}

.sidebar-header {
    margin-bottom: 36px;
}

.step-title {
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 18px;
    line-height: 1.3em;
    color: #FFFFFF;
    margin-bottom: 8px;
}

.step-description {
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 13px;
    line-height: 1.4em;
    color: rgba(255, 255, 255, 0.85);
}

.steps-list {
    display: flex;
    flex-direction: column;
}

.step-item {
    display: flex;
    flex-direction: column;
}

.step-row {
    display: flex;
    align-items: center;
    gap: 12px;
}

.step-circle {
    width: 32px;
    height: 32px;
    min-width: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 14px;
    background-color: rgba(252, 252, 252, 0.48);
    color: #FFFFFF;
    transition: all 0.3s ease;
}

.step-circle-active {
    background-color: #FFFFFF;
    color: #994C26;
}

.step-circle-completed {
    background-color: #732E0B;
    color: #FFFFFF;
}

.step-name {
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 1.3em;
    color: rgba(252, 252, 252, 0.48);
    transition: color 0.3s ease;
}

.step-name-active {
    color: #FFFFFF;
}

.step-name-completed {
    color: #FFFFFF;
}

.step-connector {
    width: 1px;
    height: 22px;
    background-color: rgba(255, 255, 255, 0.48);
    margin-left: 16px;
    margin-top: 5px;
    margin-bottom: 5px;
}

.step-connector-completed {
    background-color: #732E0B;
}

// Mobile Horizontal Stepper
.wizard-stepper-mobile {
    width: 100%;
    background: linear-gradient(135deg, #994C26 0%, #B85A2E 100%);
    border-radius: 12px 12px 0 0;
    padding: 20px 16px 24px 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.mobile-header {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 16px;
}

.progress-info {
    display: flex;
    align-items: center;
    gap: 8px;
}

.current-step-text {
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 14px;
    color: #FFFFFF;
    letter-spacing: 0.3px;
}

// Progress Bar
.progress-bar-container {
    margin-bottom: 20px;
}

.progress-bar-bg {
    width: 100%;
    height: 4px;
    background: rgba(255, 255, 255, 0.25);
    border-radius: 4px;
    overflow: hidden;
    position: relative;
}

.progress-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, #FFFFFF 0%, #FFE8D6 100%);
    border-radius: 4px;
    transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 0 8px rgba(255, 255, 255, 0.4);
}

// Horizontal Steps
.steps-horizontal {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
}

.step-item-horizontal {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

.step-circle-horizontal {
    width: 36px;
    height: 36px;
    min-width: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter', sans-serif;
    font-weight: 700;
    font-size: 15px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    
    &.step-circle-completed {
        background: #FFFFFF;
        color: #994C26;
        transform: scale(1);
        box-shadow: 0 2px 8px rgba(255, 255, 255, 0.3);
    }
    
    &.step-circle-active {
        background: #FFFFFF;
        color: #994C26;
        transform: scale(1.1);
        box-shadow: 0 4px 12px rgba(255, 255, 255, 0.4);
        animation: pulse 2s infinite;
    }
    
    &.step-circle-upcoming {
        background: rgba(255, 255, 255, 0.2);
        color: rgba(255, 255, 255, 0.6);
        border: 2px solid rgba(255, 255, 255, 0.3);
    }
}

@keyframes pulse {
    0%, 100% {
        box-shadow: 0 4px 12px rgba(255, 255, 255, 0.4);
    }
    50% {
        box-shadow: 0 4px 16px rgba(255, 255, 255, 0.6);
    }
}

.step-number {
    font-size: 15px;
}

.step-label-horizontal {
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 11px;
    line-height: 1.3em;
    text-align: center;
    color: rgba(255, 255, 255, 0.6);
    transition: all 0.3s ease;
    max-width: 80px;
    word-wrap: break-word;
    
    &.step-label-active {
        color: #FFFFFF;
        font-weight: 600;
    }
    
    &.step-label-completed {
        color: rgba(255, 255, 255, 0.9);
    }
}

// Responsive adjustments
@media (max-width: 400px) {
    .step-circle-horizontal {
        width: 32px;
        height: 32px;
        min-width: 32px;
        font-size: 13px;
    }
    
    .step-label-horizontal {
        font-size: 10px;
        max-width: 65px;
    }
    
    .steps-horizontal {
        gap: 4px;
    }
}
</style>

